import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAddCampaignContactsMutation, useCreateCampaignMutation } from "@/store/rest_api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import CampaignForm from "@/components/pages/campaigns/CampaignForm";
import LeadPicker from "@/components/LeadPicker";
import { CampaignFormData } from "@/model/interface/Campaign";
import { apiErrorMessage } from "@/util/apiError";

export default function CreateCampaignPage() {
  const navigate = useNavigate();
  const [createCampaign, { isLoading: isCreating }] = useCreateCampaignMutation();
  const [addCampaignContacts, { isLoading: isAddingLeads }] = useAddCampaignContactsMutation();
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());

  const handleSubmit = async (data: CampaignFormData) => {
    try {
      const campaign = await createCampaign(data).unwrap();
      if (!data.targetType && selectedLeadIds.size > 0) {
        try {
          await addCampaignContacts({ campaignId: campaign.id, contactIds: Array.from(selectedLeadIds) }).unwrap();
        } catch (error) {
          toast.error(apiErrorMessage(error, "The leads were not added"), {
            description: "The campaign was created. Add the leads again from the campaign page.",
          });
          navigate(`/campaigns/${campaign.id}/edit`);
          return;
        }
      }
      toast.success("Campaign created");
      navigate(`/campaigns/${campaign.id}/edit`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to create campaign"));
    }
  };

  return (
    <div className="space-y-6 p-4 max-w-3xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Campaign</h1>
        <p className="text-muted-foreground">Set up a new calling campaign</p>
      </div>

      <CampaignForm
        onSubmit={handleSubmit}
        isSubmitting={isCreating || isAddingLeads}
        submitLabel="Create Campaign"
        leadsContent={
          <Card>
            <CardHeader>
              <CardTitle>Pick leads</CardTitle>
              <CardDescription>Choose who this campaign calls. You can add more later.</CardDescription>
            </CardHeader>
            <CardContent className="flex h-[480px] flex-col">
              <LeadPicker selectedIds={selectedLeadIds} onSelectedIdsChange={setSelectedLeadIds} />
            </CardContent>
          </Card>
        }
      />
    </div>
  );
}
